import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { ChevronRight, ChevronDown, Loader2, AlertCircle } from 'lucide-react';
import { api } from '../../core/apiClient';
import { groupByChapter, SENSE_CHAPTER } from './nodeUtils';

// ─── Types ────────────────────────────────────────────────────────────────────

type ChildType = 'MATERIAL' | 'NODE';

// Etiquetes visuals agrupades (MT/MO/SV/AL); les dades subjacents (nature)
// mantenen la granularitat original (MO_PROPIA vs MO_SUBCONTRACT, SERVEIS vs
// ALTRES) perquè NodeCostEngine.ts encara les distingeix als càlculs de cost.
// MO_SUBCONTRACT comparteix text "MO" amb MO_PROPIA però amb color diferent;
// ALTRES es distingeix de SERVEIS amb text "AL" i color propi.
export function badgeFor(childType: ChildType, nature: string): { text: string; cls: string } {
  if (childType === 'NODE')          return { text: 'MT', cls: 'bg-teal-100 text-teal-800'    };
  if (nature === 'MO_PROPIA')        return { text: 'MO', cls: 'bg-violet-100 text-violet-800' };
  if (nature === 'MO_SUBCONTRACT')   return { text: 'MO', cls: 'bg-rose-100 text-rose-800'     };
  if (nature === 'SERVEIS')          return { text: 'SV', cls: 'bg-amber-100 text-amber-800'   };
  if (nature === 'ALTRES')           return { text: 'AL', cls: 'bg-orange-100 text-orange-800' };
  return                                    { text: 'MT', cls: 'bg-blue-100 text-blue-800'     };
}

export interface TreeLine {
  childType:        ChildType;
  childMaterialId?: string | null;
  childNodeId?:     string | null;
  description:      string;
  nature:           string;
  unit:             string;
  measurement:      number;
  resolvedUnitCost: number;
  lineTotal:        number;
  chapter?:         string | null;
}

interface NodeDetail {
  id:                    string;
  code?:                 string;
  name:                  string;
  cachedTotalDirectCost: number;
  cachedTotalCost:       number;
  lines:                 TreeLine[];
}

// ─── Unicode tree prefix builder ──────────────────────────────────────────────
// ancestorIsLast[d] = true when the ancestor at depth d was the last sibling

function mkPrefix(ancestorIsLast: boolean[], isLast: boolean): string {
  let s = '';
  for (const last of ancestorIsLast) s += last ? '    ' : '│   ';
  return s + (isLast ? '└─ ' : '├─ ');
}

// ─── Context threaded through recursion ───────────────────────────────────────

interface Ctx {
  collapsed:    Set<string>;
  nodeCache:    Map<string, NodeDetail>;
  loadingIds:   Set<string>;
  nodeErrors:   Map<string, string>;
  toggleBranch: (pathKey: string) => void;
  fmt2:         (n: number) => string;
}

// ─── Recursive level ──────────────────────────────────────────────────────────

interface LevelProps {
  lines:          TreeLine[];
  pathPrefix:     string;
  ancestorIsLast: boolean[];
  ancestors:      Set<string>;
  ctx:            Ctx;
}

function NodeTreeLevel({ lines, pathPrefix, ancestorIsLast, ancestors, ctx }: LevelProps) {
  const { collapsed, nodeCache, loadingIds, nodeErrors, toggleBranch, fmt2 } = ctx;
  const groups = groupByChapter(lines).sort((a, b) => {
    if (a.chapter === SENSE_CHAPTER) return 1;
    if (b.chapter === SENSE_CHAPTER) return -1;
    return 0;
  });

  // prefix used for chapter header indent (same depth as the lines, no branch char)
  const chapterPfx = ancestorIsLast.map((last) => (last ? '    ' : '│   ')).join('');

  return (
    <>
      {groups.map((group) => (
        <Fragment key={group.chapter}>
          {/* Chapter header row */}
          <div
            className="flex items-center border-t border-slate-100 bg-slate-50/60"
            style={{ fontSize: '10px', lineHeight: '16px' }}
          >
            <span
              className="shrink-0 text-slate-300 select-none"
              style={{ fontFamily: 'ui-monospace,monospace', whiteSpace: 'pre' }}
            >
              {chapterPfx}
            </span>
            <span className="flex-1 font-semibold text-slate-500 px-1 truncate">
              {group.chapter}
            </span>
            <span className="shrink-0 text-right text-slate-400 mr-2" style={{ width: '84px' }}>—</span>
            <span className="shrink-0 text-right text-slate-400 mr-2" style={{ width: '68px' }}>—</span>
            <span className="shrink-0 text-right font-medium text-slate-500" style={{ width: '80px' }}>
              {fmt2(group.items.reduce((s, { item }) => s + (item.lineTotal || 0), 0))} €
            </span>
          </div>

          {group.items.map(({ item: line, originalIdx: idx }) => {
            const isLast   = idx === lines.length - 1;
        const isNode   = line.childType === 'NODE';
        const nodeId   = line.childNodeId ?? null;
        const pathKey  = `${pathPrefix}${idx}`;
        const pfx      = mkPrefix(ancestorIsLast, isLast);
        const badge    = badgeFor(line.childType, line.nature);
        const isCycle  = isNode && nodeId ? ancestors.has(nodeId) : false;
        const isOpen   = isNode && !isCycle && nodeId ? !collapsed.has(pathKey) : false;
        const child    = nodeId ? nodeCache.get(nodeId) : undefined;
        const loading  = nodeId ? loadingIds.has(nodeId) : false;
        const errMsg   = nodeId ? nodeErrors.get(nodeId) : undefined;
        const childAnc = nodeId ? new Set([...ancestors, nodeId]) : ancestors;
        const childAIL = [...ancestorIsLast, isLast]; // ancestorIsLast for children

        // Prefix for synthetic loading/error row (acts as single last child)
        const synPfx = mkPrefix(childAIL, true);

        return (
          <div key={pathKey}>
            {/* ── Row ──────────────────────────────────────────────────── */}
            <div
              className={`flex items-center min-w-0 py-[1px] hover:bg-slate-50 ${
                isNode ? 'text-teal-900' : 'text-slate-700'
              }`}
              style={{ fontSize: '11px', lineHeight: '18px' }}
            >
              {/* Unicode tree guide */}
              <span
                className="shrink-0 text-slate-300 select-none"
                style={{ fontFamily: 'ui-monospace,monospace', whiteSpace: 'pre' }}
              >
                {pfx}
              </span>

              {/* Chevron — NODE only */}
              {isNode && !isCycle ? (
                <button
                  type="button"
                  onClick={() => toggleBranch(pathKey)}
                  className="shrink-0 text-teal-500 hover:text-teal-700 inline-flex mr-0.5"
                  style={{ padding: '1px' }}
                >
                  {collapsed.has(pathKey)
                    ? <ChevronRight className="h-3 w-3" />
                    : <ChevronDown  className="h-3 w-3" />}
                </button>
              ) : (
                <span className="shrink-0 inline-block mr-0.5" style={{ width: '14px' }} />
              )}

              {/* Badge */}
              <span
                className={`shrink-0 rounded mr-1 font-bold select-none ${badge.cls}`}
                style={{ fontSize: '8px', lineHeight: '12px', padding: '0 2px' }}
              >
                {badge.text}
              </span>

              {/* Description */}
              <span
                className="flex-1 min-w-0 truncate mr-2"
                title={line.description || '—'}
              >
                {line.description || '—'}
                {isCycle && (
                  <span className="ml-1 text-amber-500" style={{ fontSize: '9px' }}>
                    ↩ referència circular
                  </span>
                )}
              </span>

              {/* Mesura */}
              <span
                className="shrink-0 text-right text-slate-400 mr-2"
                style={{ width: '84px', fontSize: '10px' }}
              >
                {fmt2(line.measurement)} {line.unit}
              </span>

              {/* Preu Ut */}
              <span
                className={`shrink-0 text-right mr-2 ${isNode ? 'text-teal-600' : 'text-slate-500'}`}
                style={{ width: '68px', fontSize: '10px' }}
              >
                {fmt2(line.resolvedUnitCost)} €
              </span>

              {/* Import */}
              <span
                className={`shrink-0 text-right font-semibold ${
                  isNode ? 'text-teal-800 font-bold' : 'text-slate-700'
                }`}
                style={{ width: '80px' }}
              >
                {fmt2(line.lineTotal)} €
              </span>
            </div>

            {/* ── Children (when open) ─────────────────────────────────── */}
            {isNode && !isCycle && isOpen && (
              <>
                {/* Loading indicator */}
                {loading && !child && (
                  <div
                    className="flex items-center text-slate-400 italic"
                    style={{ fontSize: '10px', lineHeight: '18px' }}
                  >
                    <span
                      className="shrink-0 text-slate-200 select-none"
                      style={{ fontFamily: 'ui-monospace,monospace', whiteSpace: 'pre' }}
                    >
                      {synPfx}
                    </span>
                    <Loader2 className="h-2.5 w-2.5 animate-spin mr-1 shrink-0" />
                    carregant...
                  </div>
                )}

                {/* Error indicator */}
                {errMsg && !loading && (
                  <div
                    className="flex items-center text-red-400"
                    style={{ fontSize: '10px', lineHeight: '18px' }}
                  >
                    <span
                      className="shrink-0 text-slate-200 select-none"
                      style={{ fontFamily: 'ui-monospace,monospace', whiteSpace: 'pre' }}
                    >
                      {synPfx}
                    </span>
                    <AlertCircle className="h-2.5 w-2.5 mr-1 shrink-0" />
                    {errMsg}
                  </div>
                )}

                {/* Recursive subtree */}
                {child && (
                  child.lines.length > 0 ? (
                    <NodeTreeLevel
                      lines={child.lines}
                      pathPrefix={`${pathKey}-`}
                      ancestorIsLast={childAIL}
                      ancestors={childAnc}
                      ctx={ctx}
                    />
                  ) : (
                    <div
                      className="text-slate-400 italic"
                      style={{ fontSize: '10px', lineHeight: '18px' }}
                    >
                      <span
                        className="text-slate-200 select-none"
                        style={{ fontFamily: 'ui-monospace,monospace', whiteSpace: 'pre' }}
                      >
                        {synPfx}
                      </span>
                      (node sense línies)
                    </div>
                  )
                )}
              </>
            )}
          </div>
        );
          })}
        </Fragment>
      ))}
    </>
  );
}

// ─── Panel wrapper ────────────────────────────────────────────────────────────

export interface NodeCostTreeProps {
  lines:      TreeLine[];
  rootNodeId: string;
  rootName:   string;
}

export default function NodeCostTree({ lines, rootNodeId, rootName }: NodeCostTreeProps) {
  const [panelOpen,  setPanelOpen]  = useState(false);
  const [collapsed,  setCollapsed]  = useState<Set<string>>(new Set()); // empty = all expanded
  const [nodeCache,  setNodeCache]  = useState<Map<string, NodeDetail>>(new Map());
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const [nodeErrors, setNodeErrors] = useState<Map<string, string>>(new Map());

  // Tracks nodes already queued — prevents duplicate loads & infinite cycles
  const loadInitiated = useRef(new Set<string>());

  const fmt2 = (v: number) =>
    Number(v || 0).toLocaleString('ca-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Stable load function: only uses refs + functional setState (no stale closures)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadNode = useCallback(async (nodeId: string) => {
    if (loadInitiated.current.has(nodeId)) return;
    loadInitiated.current.add(nodeId);
    setLoadingIds((prev) => new Set(prev).add(nodeId));
    setNodeErrors((prev) => { const m = new Map(prev); m.delete(nodeId); return m; });
    try {
      const res = await api.get<{ success: boolean; data: any }>(`/nodes/${nodeId}`);
      const n = res.data;
      const detail: NodeDetail = {
        id:                    n.id,
        code:                  n.code,
        name:                  n.name,
        cachedTotalDirectCost: Number(n.cachedTotalDirectCost ?? 0),
        cachedTotalCost:       Number(n.cachedTotalCost       ?? 0),
        lines: (n.lines ?? []).map((l: any): TreeLine => ({
          childType:        l.childType,
          childMaterialId:  l.childMaterialId  ?? null,
          childNodeId:      l.childNodeId      ?? null,
          description:      l.description      ?? '',
          nature:           l.nature           ?? 'MATERIAL',
          unit:             l.unit             ?? 'Ut',
          measurement:      Number(l.measurement      ?? 0),
          resolvedUnitCost: Number(l.resolvedUnitCost ?? 0),
          lineTotal:        Number(l.lineTotal        ?? 0),
          chapter:          l.chapter ?? null,
        })),
      };
      setNodeCache((prev) => new Map(prev).set(nodeId, detail));
      // Cascade: auto-load child NODEs (idempotent via loadInitiated)
      for (const l of detail.lines) {
        if (l.childType === 'NODE' && l.childNodeId) {
          loadNode(l.childNodeId);
        }
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? 'Error en carregar';
      setNodeErrors((prev) => new Map(prev).set(nodeId, msg));
    } finally {
      setLoadingIds((prev) => { const s = new Set(prev); s.delete(nodeId); return s; });
    }
  }, []); // stable: refs + functional setters only

  // When panel opens (or root lines change), kick off loading of root-level NODEs
  useEffect(() => {
    if (!panelOpen) return;
    lines.forEach((l) => {
      if (l.childType === 'NODE' && l.childNodeId) loadNode(l.childNodeId);
    });
  }, [panelOpen, lines, loadNode]);

  const toggleBranch = (pathKey: string) => {
    setCollapsed((prev) => {
      const s = new Set(prev);
      s.has(pathKey) ? s.delete(pathKey) : s.add(pathKey);
      return s;
    });
  };

  const ctx: Ctx = { collapsed, nodeCache, loadingIds, nodeErrors, toggleBranch, fmt2 };
  const total = lines.reduce((s, l) => s + (l.lineTotal || 0), 0);

  return (
    <div className="border rounded">
      {/* Collapsible panel header */}
      <button
        type="button"
        onClick={() => setPanelOpen((v) => !v)}
        className="w-full flex items-center gap-2 bg-slate-100 px-4 py-2 font-medium text-sm hover:bg-slate-200 transition-colors rounded-t"
      >
        {panelOpen
          ? <ChevronDown  className="h-4 w-4" />
          : <ChevronRight className="h-4 w-4" />}
        <span>Arbre de costos</span>
      </button>

      {panelOpen && (
        <div className="overflow-x-auto">
          <div style={{ minWidth: '580px' }}>

            {/* Column headers */}
            <div
              className="flex items-center bg-slate-50 border-b px-2 py-0.5 text-slate-400 select-none"
              style={{ fontSize: '10px' }}
            >
              <span className="flex-1">Descripció</span>
              <span className="text-right mr-2" style={{ width: '84px' }}>Mesura</span>
              <span className="text-right mr-2" style={{ width: '68px' }}>Preu Ut</span>
              <span className="text-right"      style={{ width: '80px' }}>Import</span>
            </div>

            {/* Root node header row */}
            <div
              className="flex items-center px-2 py-[3px] bg-slate-50 border-b font-semibold text-slate-800"
              style={{ fontSize: '12px' }}
            >
              <span className="flex-1 min-w-0 truncate" title={rootName}>
                {rootName}
              </span>
              <span className="text-right text-slate-400 mr-2" style={{ width: '84px', fontSize: '10px' }}>—</span>
              <span className="text-right text-slate-400 mr-2" style={{ width: '68px', fontSize: '10px' }}>—</span>
              <span className="text-right font-bold" style={{ width: '80px' }}>
                {fmt2(total)} €
              </span>
            </div>

            {/* Tree */}
            {lines.length === 0 ? (
              <div className="px-4 py-3 text-sm text-slate-400 italic">
                Cap línia al descompost.
              </div>
            ) : (
              <NodeTreeLevel
                lines={lines}
                pathPrefix=""
                ancestorIsLast={[]}
                ancestors={new Set([rootNodeId])}
                ctx={ctx}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
